import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import AutoSizer from "react-virtualized-auto-sizer"
import { VariableSizeList as List } from "react-window"

import SearchIcon from "@mui/icons-material/Search"
import { Grid, useMediaQuery, useTheme } from "@mui/material"
import { DateRangePicker, InputText, Typography } from "@synapse-analytics/synapse-ui"

import { useWindowSize } from "../../hooks/useWindowResize"
import { KonanTimeHelper } from "../../utils/genericHelpers"
import { LoadingContainer } from "../core/LoadingContainer"
import { KonanRowRender } from "../utility/KonanRowRender"

import styles from "./LogViewer.module.scss"

type Props = {
  data: string[]
  isLoading?: boolean
  enableSearch?: boolean
  customBorderRadius?: string
  minHeight?: string
  enableDatePicker?: boolean
  startDate?: moment.Moment | null
  endDate?: moment.Moment | null
  onStartDateChange?: React.Dispatch<React.SetStateAction<moment.Moment | null>>
  onEndDateChange?: React.Dispatch<React.SetStateAction<moment.Moment | null>>
  isOutSideRange?: (date: moment.Moment) => boolean
}

/**
 * A log viewer component that displays a list of logs. It allows the user to filter
 * the logs by searching for a specific string. It also allows the user to jump to the
 * bottom of the log list by clicking on the "Jump to bottom" button.
 *
 * @param {string[]} data - The list of logs to be displayed.
 * @param {boolean} isLoading - If true, a loading indicator will be displayed.
 * @param {boolean} enableSearch - If true, a search bar will be displayed.
 * @param {string} customBorderRadius - The border radius of the log viewer.
 * @param {string} minHeight - The minimum height of the log viewer.
 * @param {moment.Moment | null} startDate - The start date of the logs to be displayed.
 * @param {moment.Moment | null} endDate - The end date of the logs to be displayed.
 * @param {React.Dispatch<React.SetStateAction<moment.Moment | null>>} onStartDateChange -
 * The function to be called when the start date changes.
 * @param {React.Dispatch<React.SetStateAction<moment.Moment | null>>} onEndDateChange -
 * The function to be called when the end date changes.
 * @param {(date: moment.Moment) => boolean} isOutSideRange - A function that takes a date
 * and returns true if the date is outside the range of the logs to be displayed.
 */

export function LogViewer(props: Readonly<Props>): React.ReactElement {
  const {
    data,
    enableDatePicker,
    isLoading,
    enableSearch = true,
    customBorderRadius,
    minHeight,
    endDate,
    onEndDateChange,
    onStartDateChange,
    isOutSideRange,
    startDate,
  } = props

  const [windowWidth] = useWindowSize()

  const MuiTheme = useTheme()

  const isSmDown = useMediaQuery(MuiTheme.breakpoints.down("sm"))

  const { t } = useTranslation("dataDisplay")

  const konanTime = new KonanTimeHelper()

  const logRef = useRef<List>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sizeMap = useRef<any>({})

  const [searchValue, setSearchValue] = useState<string>("")

  const [isAtBottom, setIsAtBottom] = useState<boolean>(true)
  const [jumpedOnce, setJumpedOnce] = useState<boolean>(false)

  const modifiedData = useMemo(() => {
    return data.filter((s) => s.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())) || ""
  }, [data, searchValue])

  const setSize = useCallback((index: number, size: number) => {
    if (sizeMap?.current) {
      sizeMap.current = { ...sizeMap.current, [index]: size }
      if (logRef.current) {
        logRef.current.resetAfterIndex(index)
      }
    }
  }, [])

  const getInputWidth = useCallback(() => {
    if (gridRef.current) {
      const parentWidth = gridRef.current.offsetWidth
      return !isSmDown ? Math.floor(parentWidth * 0.5) : parentWidth
    }
    return 300 // fallback to original width
  }, [isSmDown])

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const getSize = (index: number) => {
    if (sizeMap.current[index]) {
      return sizeMap.current[index]
    } else {
      return 20
    }
  }

  // Jump to bottom once component mounts
  useEffect(() => {
    if (logRef.current && modifiedData.length > 0 && !isAtBottom && !jumpedOnce) {
      logRef.current.scrollToItem(modifiedData?.length)
      setJumpedOnce(true)
      setIsAtBottom(true)
    }
  }, [modifiedData, isAtBottom, jumpedOnce])

  // Update scroll position to follow any new logs if user is already at bottom
  useEffect(() => {
    if (logRef.current && isAtBottom) {
      logRef.current.scrollToItem(modifiedData?.length)
    }
  }, [modifiedData.length, isAtBottom])

  return (
    <React.Fragment>
      <Grid
        item
        style={{
          padding: enableSearch || enableDatePicker ? "0px 0px 12px 0px" : "0px",
        }}
        display="flex"
        flexWrap={"wrap-reverse"}
        gap={1}
        justifyContent="space-between"
        ref={gridRef}
      >
        {enableSearch && (
          <InputText
            value={searchValue}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            handleChange={(e: any) => setSearchValue(e.target.value)}
            placeholder={t("logViewer.searchPlaceholder")}
            disabled={isLoading}
            variant="filled"
            width={getInputWidth()}
            hideDescription
            startAdornment={
              <SearchIcon
                fontSize="small"
                style={{
                  paddingTop: "2px",
                  fill: "var(--base-text-2)",
                }}
              />
            }
          />
        )}

        {enableDatePicker && onStartDateChange && onEndDateChange && (
          <Grid item alignSelf="center">
            <DateRangePicker
              key="Model logs"
              startDate={startDate as moment.Moment | null}
              endDate={endDate as moment.Moment | null}
              onStartDateChange={onStartDateChange}
              onEndDateChange={(endDate: moment.Moment | null) => onEndDateChange(konanTime.adjustDate(endDate, "end"))}
              // TODO:: fix in SUI to enable both options at the same time
              // disableFuture
              isOutsideRange={isOutSideRange}
            />
          </Grid>
        )}
      </Grid>

      <Grid
        item
        xs={12}
        className={`card-box-shadow ${styles.paper}`}
        style={{
          borderRadius: customBorderRadius ? `${customBorderRadius}` : "4px",
          minHeight: minHeight ?? "200px !important",
        }}
      >
        {isLoading ? (
          <LoadingContainer />
        ) : !modifiedData?.length ? (
          <Grid container direction="row" justifyContent="center" alignItems="center">
            <Typography variant="label-bold" variantColor={2}>
              {t("logViewer.noLogsMessage")}
            </Typography>
          </Grid>
        ) : (
          // manages width and height properties to fill available space
          <AutoSizer>
            {({ height, width }) => (
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              <List
                className="scrollbar"
                height={height}
                width={width}
                itemCount={modifiedData.length}
                itemSize={getSize}
                ref={logRef}
                onItemsRendered={({ visibleStopIndex }) => {
                  if (visibleStopIndex === modifiedData?.length - 1) {
                    setIsAtBottom(true)
                  } else {
                    setIsAtBottom(false)
                  }
                }}
              >
                {({ index, style }) => (
                  <div style={style}>
                    <KonanRowRender
                      modifiedData={modifiedData}
                      searchValue={searchValue}
                      index={index}
                      style={style}
                      setSize={setSize}
                      windowWidth={windowWidth}
                    />
                  </div>
                )}
              </List>
            )}
          </AutoSizer>
        )}
      </Grid>
    </React.Fragment>
  )
}
